/**
 * `<StaleBanner />` — Story 5.4 (UX §3.3.3 + UX §4.1 line 886, AC line 1013).
 *
 * Yellow callout shown above content when polling reports stale data.
 * Forward-pair: Story 7.2 admin dashboard mounts `<StaleBanner />` when
 * Story 5.7's `usePolling` returns `isStale: true`.
 *
 * Layout:
 *   - Full-width banner.
 *   - `--warning` background; `--text-on-brand` foreground.
 *   - `--space-3` vertical padding; `--space-4` horizontal.
 *   - `--radius-md` border-radius.
 *
 * Aria role: `role="status"` (non-blocking; UX §5.1 line 999).
 *
 * Story 17.1 - adds optional `variant: 'warning' | 'danger'` (default
 * 'warning'). The 'danger' variant is used by `useEventsList` consumers
 * to signal "couldn't refresh - retrying" over cached data; it switches
 * the background to `--danger` and the role to `role="alert"` so screen
 * readers announce it more assertively than the default warning role.
 *
 * Default copy is a static string; consumer overrides via `children`
 * to inject the timestamp from the polling hook.
 */
import type { ReactNode } from 'react';

export type StaleBannerVariant = 'warning' | 'danger';

export interface StaleBannerProps {
  children?: ReactNode;
  variant?: StaleBannerVariant;
}

const DEFAULT_COPY = 'Updates paused. Last update may be stale.';

/**
 * `StaleBanner` - non-blocking callout for stale-data ('warning') or
 * refresh-failure ('danger') state.
 */
export function StaleBanner({ children, variant = 'warning' }: StaleBannerProps) {
  const isDanger = variant === 'danger';
  return (
    <div
      role={isDanger ? 'alert' : 'status'}
      data-testid="stale-banner"
      data-variant={variant}
      style={{
        background: isDanger ? 'var(--danger)' : 'var(--warning)',
        color: 'var(--text-on-brand)',
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-md)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm-size)',
        textAlign: 'center',
        width: '100%',
      }}
    >
      {children ?? DEFAULT_COPY}
    </div>
  );
}
